"use client";

import { UpgradeSuccessDialog } from "@/components/billing/upgrade-success/upgrade-success-dialog";
import { AppBar } from "@/components/navigation/app-bar";
import { ComputePausedBanner } from "@/components/navigation/compute-paused-banner";
import { SIDEBAR_WIDTH_VARS } from "@/components/navigation/sidebar-v2";
import { MobileNavDrawer } from "@/components/navigation/sidebar-v2/mobile-nav-drawer";
import { SidebarProvider } from "@/components/ui/sidebar";

import { LoadingState } from "@/components/loading-state";
import { redirectToSignIn } from "@/lib/auth/redirect-utils";
import { routes } from "@/lib/navigation/routes";
import { useWorkspace } from "@/providers/workspace-provider";
import { EmptyState, EmptyStateDescription, EmptyStateHeader, EmptyStateTitle } from "@teral/ui";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { Suspense, useEffect } from "react";
import { QueryTimeProvider } from "../../providers/query-time-provider";

interface LayoutProps {
  children: React.ReactNode;
}

type WorkspaceLike = { id: string; enabled: boolean };

function WorkspaceContent({
  workspace,
  children,
}: {
  workspace: WorkspaceLike;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full flex-1 flex flex-col">
      {workspace.enabled ? (
        <QueryTimeProvider>{children}</QueryTimeProvider>
      ) : (
        <div className="flex flex-1 items-center justify-center p-12">
          <EmptyState>
            <EmptyStateHeader>
              <EmptyStateTitle>This workspace is disabled</EmptyStateTitle>
              <EmptyStateDescription>
                Contact{" "}
                <Link
                  href={`mailto:support@teral.dev?body=workspaceId: ${workspace.id}`}
                  className="underline"
                >
                  support@teral.dev
                </Link>
              </EmptyStateDescription>
            </EmptyStateHeader>
          </EmptyState>
        </div>
      )}
    </div>
  );
}

export default function Layout({ children }: LayoutProps) {
  const router = useRouter();
  const pathname = usePathname();
  const { user, workspace, isLoading, error, workspaceMissing } = useWorkspace();
  // Creation wizards are focused full-screen experiences without the sidebar.
  const isCreationWizard =
    /\/projects\/[^/]+\/apps\/new$/.test(pathname) || /\/settings\/logdrains\/new$/.test(pathname);

  useEffect(() => {
    // Don't navigate while loading
    if (isLoading) {
      return;
    }

    // Handle authentication errors
    const isAuthError = error?.data?.code === "UNAUTHORIZED" || error?.data?.code === "FORBIDDEN";

    if (isAuthError) {
      redirectToSignIn(window.location);
      return;
    }

    // Handle cases where user needs workspace setup
    // Case 1: User exists but no orgId or role (incomplete setup)
    // Case 2: No workspace exists for the org (WorkOS org without workspace)
    if (user && (!user.orgId || workspaceMissing)) {
      router.push(routes.workspaces.create());
      return;
    }
  }, [user, isLoading, error, workspaceMissing, router]);

  // Show loading state while checking authentication and workspace
  if (isLoading || !user || !workspace) {
    return (
      <div className="h-dvh flex flex-col">
        <LoadingState delayMs={300} message="Loading workspace..." />
      </div>
    );
  }

  return (
    <SidebarProvider style={SIDEBAR_WIDTH_VARS}>
      <Suspense fallback={null}>
        <UpgradeSuccessDialog />
      </Suspense>
      <div className="h-dvh w-full flex flex-col overflow-hidden bg-background">
        <ComputePausedBanner />
        <MobileNavDrawer />
        {/* Reserve the scrollbar gutter so content doesn't shift horizontally
            when the scrollbar appears/disappears (e.g. a dialog locking scroll
            or content height changing). Without this the centered layout
            "shakes" and buttons move out from under the cursor (ENG-2884). */}
        <div className="flex-1 overflow-auto" style={{ scrollbarGutter: "stable" }}>
          {/* The app bar sits in the scroller so it stays pinned while the page
              scrolls beneath its translucent surface. Creation wizards keep the
              path but drop the section tabs. */}
          <AppBar showSections={!isCreationWizard} />
          <div
            className="isolate w-full min-h-[calc(100%-3.25rem)] flex flex-col items-center bg-background"
            id="layout-wrapper"
          >
            <WorkspaceContent workspace={workspace}>{children}</WorkspaceContent>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
}
